import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogDescription,
} from '../common/Catalyst/dialog';
import { Button } from '../common/Catalyst/button';
import { UserReturn } from '../clients/rust-api-client';
import { userService } from '../clients/ApiClients';
import { ErrorAlert } from '../common/Custom/ErrorAlert';
import { apiErrorMessage, apiErrorStatus } from '../utils/apiErrors';

interface Props {
  user: UserReturn;
  onClose: () => void;
  onReset: () => void;
}

/**
 * Clear someone's enrolled authentication factors — the lost-phone path.
 *
 * **This lowers the bar to an account**, which is why it is SuperAdmin
 * only and why it asks for confirmation rather than acting on one click.
 * Until the person enrols again, their account is protected by their
 * WorkOS credential alone.
 *
 * WorkOS's deletion cannot be undone: there is no "restore factor". The
 * person re-enrols from scratch at their next sign-in, which is exactly
 * what is wanted when the device is gone, and a real cost if this was
 * done to the wrong row.
 */
const ResetMfaDialog: React.FC<Props> = ({ user, onClose, onReset }) => {
  const [error, setError] = useState<string>('');
  const [working, setWorking] = useState<boolean>(false);

  const handleReset = async () => {
    setError('');
    setWorking(true);
    try {
      await userService.clearMfaFactors({ id: user.id });
      onReset();
    } catch (err) {
      const status = apiErrorStatus(err);
      if (status === 400) {
        // An account that has never signed in through WorkOS has no
        // identity there, so it has no factors either. Nothing is wrong;
        // there is simply nothing to clear.
        setError(
          'This account has not signed in through WorkOS yet, so it has no authenticator to reset.'
        );
      } else {
        setError(apiErrorMessage(err, "Couldn't reset the authenticator."));
      }
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle>Reset multi-factor authentication</DialogTitle>
      <DialogDescription>
        For {user.full_name} ({user.email}).
      </DialogDescription>
      <DialogBody>
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-900 dark:bg-red-950/40 dark:text-red-200">
          <p>
            This removes every authenticator enrolled on this account and
            cannot be undone.
          </p>
          <p className="mt-2">
            Until they enrol a new one, this account is protected by its
            password alone. Only do this for someone you have identified by
            another means.
          </p>
        </div>
        {/*
          Not Catalyst's `ErrorMessage`: that belongs to a `Field`, and
          this dialog has no form control to attach it to — it is a
          confirmation, not a form. `fieldset.guard.test.ts` enforces the
          distinction.
        */}
        <ErrorAlert className="mt-4">{error}</ErrorAlert>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={working}>
          Cancel
        </Button>
        <Button color="red" onClick={handleReset} disabled={working}>
          {working ? 'Resetting…' : 'Reset authenticator'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResetMfaDialog;
